import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../books/mutations";
import type { SurveyState, SurveySubmission } from "./types";

const URL = "/api/onboarding-survey/";
const QUERY_KEY = ["onboardingSurvey"];

/**
 * Fetched lazily: `myself.onboardingSurveyDue` already tells the gate whether to open, so
 * the answers and option lists cost nothing until the dialog is actually on screen.
 *
 * Deliberately no `placeholderData` — that is what makes `useUser()`'s placeholder
 * indistinguishable from a real payload, and this query has no need for it.
 */
export function useOnboardingSurvey(enabled: boolean) {
  return useQuery<SurveyState>({
    queryKey: QUERY_KEY,
    queryFn: () => fetch(URL).then((res) => res.json()),
    enabled,
  });
}

/**
 * Invalidating `myself` is not optional: it flips `onboardingSurveyDue` off and, through
 * the effect in `useUser()`, re-runs `identifyUser()` so the new cluster reaches Mixpanel.
 */
function useSurveyMutation(
  request: (body?: SurveySubmission) => Promise<SurveyState>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (state: SurveyState) => {
      queryClient.setQueryData(QUERY_KEY, state);
      queryClient.invalidateQueries({ queryKey: ["myself"] });
    },
  });
}

export function useSubmitSurvey() {
  return useSurveyMutation((body) => apiRequest(URL, "PUT", body));
}

export function useSnoozeSurvey() {
  return useSurveyMutation(() => apiRequest(`${URL}snooze/`, "POST"));
}
